import React from 'react';
import { Card, Grid, Box, Typography, Chip, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import { useScrollAnimation, fadeInUp } from '../hooks/useScrollAnimation';

const ProjectItem = ({ project, index }) => {
  const isEven = index % 2 === 0;
  const [ref, controls] = useScrollAnimation(0.2);

  return (
    <Card
      component={motion.div}
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      whileHover={{ 
        y: -8, 
        transition: { duration: 0.3 } 
      }}
      sx={{
          mb: 6,
          background: 'var(--card-bg)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border-subtle)',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-8px)',
            boxShadow: '0 24px 64px rgba(0, 245, 255, 0.2)',
          },
        }}
      >
        <Grid container>
          <Grid item xs={12} md={6} order={{ xs: 1, md: isEven ? 1 : 2 }}>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                minHeight: { xs: 250, md: 400 },
                background: project.image
                  ? `url(${project.image})`
                  : 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(255, 107, 53, 0.2))',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {project.image ? (
                <Box
                  component="img"
                  src={project.image}
                  alt={project.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.08)',
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <CodeIcon sx={{ fontSize: 80, opacity: 0.3 }} />
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6} order={{ xs: 2, md: isEven ? 2 : 1 }}>
            <CardContent
              sx={{
                p: { xs: 4, md: 6 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                {project.title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  lineHeight: 1.8,
                }}
              >
                {project.description}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {project.tags.map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    sx={{
                      background: 'rgba(0, 245, 255, 0.1)',
                      color: 'primary.main',
                      fontFamily: "'Fira Code', monospace",
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>
  );
};

export default ProjectItem;
